import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { SponsorshipEditor } from './sponsorship-editor';
import { useSponsorships } from './sponsorships.api';
import {
  emptySponsorship,
  statusOf,
  type Sponsorship,
  type SponsorshipInput,
  type SponsorshipStatus,
} from './types';

/**
 * Sponsorships — a plan per row, opened to attach items and stores.
 *
 * The list deliberately says nothing about whether a sponsorship has been
 * applied to the catalogue: nothing on the record records that, so a badge
 * claiming it would be a guess. The window is what the row can honestly show.
 */

const ANY = 'ANY';

const STATUS_TONE: Record<SponsorshipStatus, 'success' | 'secondary' | 'outline'> =
  {
    running: 'success',
    scheduled: 'secondary',
    finished: 'outline',
  };

export function SponsorshipsPage() {
  const { t } = useTranslation();

  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState(ANY);
  const [editing, setEditing] = useState<SponsorshipInput | null>(null);

  const list = useSponsorships();
  const sponsorships = useMemo(() => list.data ?? [], [list.data]);

  const counts = useMemo(() => {
    const tally: Record<SponsorshipStatus, number> = {
      running: 0,
      scheduled: 0,
      finished: 0,
    };
    for (const item of sponsorships) tally[statusOf(item)] += 1;
    return tally;
  }, [sponsorships]);

  const term = draft.trim().toLowerCase();
  const rows = useMemo(
    () =>
      sponsorships.filter((item) => {
        if (status !== ANY && statusOf(item) !== status) return false;
        if (!term) return true;
        return (item.name ?? '').toLowerCase().includes(term);
      }),
    [sponsorships, status, term]
  );

  const fields: FilterField[] = [
    {
      id: 'status',
      label: t('sponsorships.status'),
      value: status,
      onChange: setStatus,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('sponsorships.statusAny') },
        {
          value: 'running',
          label: `${t('sponsorships.statusRunning')} (${counts.running})`,
        },
        {
          value: 'scheduled',
          label: `${t('sponsorships.statusScheduled')} (${counts.scheduled})`,
        },
        {
          value: 'finished',
          label: `${t('sponsorships.statusFinished')} (${counts.finished})`,
        },
      ],
    },
  ];

  const StatusCell = ({ row }: { row: Sponsorship }) => {
    const state = statusOf(row);
    return (
      <Badge variant={STATUS_TONE[state]}>
        {t(`sponsorships.status${state[0].toUpperCase()}${state.slice(1)}`)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('sponsorships.title')}
        description={t('sponsorships.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={() => setEditing(emptySponsorship())}>
              <Plus />
              <span className="hidden sm:inline">
                {t('sponsorships.newSponsorship')}
              </span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={list.isFetching}
        search={
          <div className="relative flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('sponsorships.searchPlaceholder')}
              aria-label={t('sponsorships.searchPlaceholder')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={fields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('sponsorships.showingOf', {
                shown: rows.length,
                total: sponsorships.length,
              })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('sponsorships.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-56" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {sponsorships.length === 0
              ? t('sponsorships.none')
              : t('sponsorships.noneMatch')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border md:block">
            <Table busy={list.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('sponsorships.name')}</TableHead>
                  <TableHead>{t('sponsorships.window')}</TableHead>
                  <TableHead>{t('sponsorships.status')}</TableHead>
                  <TableHead className="w-24 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      {row.name || '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(row.startDate)} – {formatDate(row.endDate)}
                    </TableCell>
                    <TableCell>
                      <StatusCell row={row} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" asChild>
                        <Link to={`/sponsorships/${row.id}`}>
                          {t('sponsorships.open')}
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative md:hidden">
            <BusyOverlay show={list.isFetching} />
            <div
              className={cn(
                'space-y-2',
                list.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 truncate font-medium">
                      {row.name || '—'}
                    </p>
                    <StatusCell row={row} />
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {formatDate(row.startDate)} – {formatDate(row.endDate)}
                  </p>
                  <Button size="sm" className="w-full" asChild>
                    <Link to={`/sponsorships/${row.id}`}>
                      {t('sponsorships.open')}
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <SponsorshipEditor
        sponsorship={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}
